// gate-registry-transitions.ts
//
// approve/waive/invalidate state transitions for gate-registry.ts (split out
// to keep both modules ≤200 lines — see gate-registry.ts's header comment).
//
// Rules enforced here (F07):
//  - approve() only records when requestId matches the CURRENT pending
//    request AND actor is bound — wrong actor/request rejected with a hint
//    naming the current request (no silent guess).
//  - duplicate approve of an ALREADY-approved gate (same request id) returns
//    the ORIGINAL receipt — no double-apply.
//
// waiveGate/invalidateGateOnShaChange split further into
// gate-registry-waive-invalidate.ts to keep this file ≤200 lines —
// re-exported so callers only import ONE module for "gate transitions".

import { carryAuthorityGrant } from "./gate-authority-grant-carryover.js";
import {
  type GateId,
  type GateReceipt,
  type GateRecord,
  GateRejectedError,
  readGateFile,
  writeGateFile,
} from "./gate-registry.js";

export {
  type GateWaiveInput,
  invalidateGateOnShaChange,
  waiveGate,
} from "./gate-registry-waive-invalidate.js";

export interface GateApproveInput {
  gate: GateId;
  requestId: string;
  actorId: string;
  boundActorId: string;
  artifactHash?: string;
  now?: () => Date;
  /**
   * E1/H1: phạm vi quyền người duyệt cấp — `targets` là danh sách môi trường
   * được phép deploy, `expiresAt` là ISO timestamp hết hạn. Đọc bởi
   * `deployment-adapter.ts` `recheckG3ImmediatelyBeforeFirstSideEffect`.
   *
   * Optional: approve không khai ⇒ GIỮ giá trị đã có trên record (xem hàm
   * bên dưới), không khai ở đâu cả ⇒ gate không bị siết, đúng hành vi cũ.
   */
  targets?: string[];
  expiresAt?: string;
}

/**
 * Approve a gate. Rejects with GateRejectedError (carrying currentRequestId as
 * a hint) when the request id doesn't match the current pending request, or
 * when the actor isn't the bound actor. Duplicate approve of the SAME request
 * id on an already-approved gate returns the original receipt (reused=true),
 * no double-apply.
 */
export async function approveGate(
  controlRoot: string,
  input: GateApproveInput,
): Promise<GateReceipt> {
  const gates = await readGateFile(controlRoot);
  const record = gates[input.gate];

  // Deliberate: this early return does NOT compare `input.actorId` against the
  // record before handing back the receipt. That is safe because the receipt
  // echoes the ORIGINAL approver's `record.actor_id`, never the caller's — so a
  // wrong actor replaying a request id learns nothing and impersonates nobody;
  // it just gets the existing decision back verbatim. Were a future caller to
  // start inferring "who am I" from a returned receipt, this would have to move
  // below the bound-actor check instead.
  if (record.status === "approved" && record.request_id === input.requestId) {
    return {
      gate: input.gate,
      status: "approved",
      request_id: record.request_id,
      actor_id: record.actor_id,
      at: record.at,
      reused: true,
    };
  }

  if (input.actorId !== input.boundActorId) {
    throw new GateRejectedError(
      `Actor "${input.actorId}" không phải actor đã bind ("${input.boundActorId}") cho gate ${input.gate}.`,
      record.request_id,
    );
  }

  if (record.status !== "pending" || record.request_id !== input.requestId) {
    throw new GateRejectedError(
      `Request ID "${input.requestId}" không khớp request hiện hành của gate ${input.gate}.`,
      record.request_id,
    );
  }

  const now = input.now ?? (() => new Date());
  const next: GateRecord = {
    status: "approved",
    request_id: input.requestId,
    artifact_hash: input.artifactHash ?? record.artifact_hash,
    actor_id: input.actorId,
    at: now().toISOString(),
    ...carryAuthorityGrant(record, input),
  };
  gates[input.gate] = next;
  // OCC (B1): `gates.revision` is what this function READ at the top — a
  // directive approve racing the supervisor loses here instead of silently
  // overwriting whatever the other writer just committed.
  await writeGateFile(controlRoot, gates, gates.revision);

  return {
    gate: input.gate,
    status: "approved",
    request_id: next.request_id,
    actor_id: next.actor_id,
    at: next.at,
    reused: false,
  };
}
